// Reprint a past bill from Fashion's Orders. Mirrors SuperMarket's executeReprint
// (useSuperMarketActions): same printer choice from the till's print setup, the
// "Duplicate Copy" label, the "Receipt Reprint Request" approval rule, and the
// RRC POS log when no rule applies. The bill data comes from Core's
// getSalesHistoryData, as on SuperMarket.
import HCPrint from "../../lib/printer/hardWareControllerPrinter";
import CWHCPrint from "../../lib/printer/CWHardwareController";
import { xmlPrint } from "../../lib/printer/xmlPrinter";
import { htmlPrint } from "../../lib/printer/htmlPrinter";
import { getWorkflowData } from "../getWorkflowData";

const DEFAULT_PRINTERS = { HCPrint, CWHCPrint, xmlPrint, htmlPrint };

export function reprintMode(tillData) {
  const till = tillData?.tillAccess?.cwrTill;
  if (till?.printTemplate?.obController === "Y") return "HCPrint";
  if (till?.printTemplate?.cwController === "Y") return "CWHCPrint";
  if (till?.hardwareController?.printReceipt === "Y") return "xmlPrint";
  return "htmlPrint";
}

// posConfig.reprintRules can block reprinting bills with listed categories/products.
export function reprintBlocked(order, posConfig) {
  let rules = {};
  try {
    rules = posConfig?.reprintRules ? JSON.parse(posConfig.reprintRules) : {};
  } catch {
    rules = {};
  }
  if (rules?.restrict_reprint !== true) return false;
  const categories = rules?.rules?.categories || [];
  const products = rules?.rules?.products || [];
  return (order?.items || []).some((item) => categories.includes(item?.mProductCategoryId) || products.includes(item?.productId));
}

// Resolves { ok, message, tone }. Never throws.
export async function reprintOrder(order, { tillData, posConfig, dispatchEvent, upsertPOSLog, printers = DEFAULT_PRINTERS } = {}) {
  if (!order) return { ok: false, tone: "bad", message: "This bill couldn't be loaded for reprint." };
  if (reprintBlocked(order, posConfig)) return { ok: false, tone: "warn", message: "Reprint isn't allowed for bills with service items." };
  const data = { ...order, isReprint: true };
  const print = () => printers[reprintMode(tillData)](data, "Duplicate Copy");
  try {
    const { workflowRules, approvers } = await getWorkflowData();
    const rule = (workflowRules || []).find((r) => r.eventName === "Receipt Reprint Request");
    if (rule && typeof dispatchEvent === "function") {
      return await new Promise((resolve) => {
        dispatchEvent(rule.eventName, rule.ruleName, rule.ruleCondition, approvers, null, (eventData) => {
          if (!eventData?.approvalGranted) {
            resolve({ ok: false, tone: "warn", message: "The reprint wasn't approved." });
            return;
          }
          try {
            print();
            resolve({ ok: true, tone: "ok", message: `Sent ${order.documentno} to the receipt printer.` });
          } catch {
            resolve({ ok: false, tone: "bad", message: "Couldn't reach the receipt printer. Check it and try again." });
          }
        });
      });
    }
    print();
    upsertPOSLog?.(order, "RRC");
    return { ok: true, tone: "ok", message: `Sent ${order.documentno} to the receipt printer.` };
  } catch {
    return { ok: false, tone: "bad", message: "Couldn't reach the receipt printer. Check it and try again." };
  }
}
